import { useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { RefreshCcw, Download, Upload } from "lucide-react";

export default function OwnerEbay() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const importOne = async () => {
    if (!url) return;
    setLoading(true);
    try {
      const { data } = await api.post("/admin/ebay/import", { ebay_url: url });
      toast.success(data.message || "Draft created — populate the remaining fields in the product editor.");
      setUrl("");
    } catch (e) { toast.error("Could not import. " + (e?.response?.data?.detail || "")); }
    finally { setLoading(false); }
  };

  const syncAll = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/ebay/sync-all");
      toast.success(data.message || `Synchronized ${data.synced} products.`);
    } catch { toast.error("Could not synchronize."); }
    finally { setSyncing(false); }
  };

  return (
    <div data-testid="owner-ebay-page">
      <h1 className="font-display text-3xl font-extrabold text-white">eBay Sync</h1>
      <p className="text-slate-400 mt-1 text-sm max-w-2xl">Import a single eBay listing by URL, or trigger a bulk synchronization on all eBay-linked products.</p>

      <div className="mt-6 grid lg:grid-cols-2 gap-4">
        <section className="rounded-xl border border-white/10 bg-white/5 p-5">
          <div className="flex items-center gap-2"><Download className="w-4 h-4 text-[#0055FF]" /><h2 className="font-heading text-white font-semibold">Import single listing</h2></div>
          <Label className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mt-4 block">eBay listing URL</Label>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.ebay.com/itm/…" className="mt-1.5 bg-white/5 border-white/10 text-white font-mono" data-testid="ebay-import-url" />
          <Button onClick={importOne} disabled={loading} className="mt-4 bg-[#0055FF] hover:bg-[#0044CC]" data-testid="ebay-import-submit">
            {loading ? "Importing…" : "Create draft product"}
          </Button>
          <p className="mt-3 text-xs text-slate-500 leading-relaxed">
            The full eBay API integration structure is in place. Populate the remaining fields via the product editor.
            To enable automatic field extraction, add your eBay developer credentials to the backend .env.
          </p>
        </section>

        <section className="rounded-xl border border-white/10 bg-white/5 p-5">
          <div className="flex items-center gap-2"><RefreshCcw className="w-4 h-4 text-[#0055FF]" /><h2 className="font-heading text-white font-semibold">Synchronize all products</h2></div>
          <p className="mt-4 text-sm text-slate-300">Refreshes the `last_synced` timestamp on all eBay-linked products that don't have a manual override.</p>
          <Button onClick={syncAll} disabled={syncing} className="mt-4 bg-[#0055FF] hover:bg-[#0044CC]" data-testid="ebay-sync-all">
            {syncing ? "Synchronizing…" : "Synchronize all"}
          </Button>
        </section>

        <section className="rounded-xl border border-white/10 bg-white/5 p-5 lg:col-span-2">
          <div className="flex items-center gap-2"><Upload className="w-4 h-4 text-[#0055FF]" /><h2 className="font-heading text-white font-semibold">CSV import / export</h2></div>
          <p className="mt-3 text-sm text-slate-300">Bulk-manage products via CSV. Both endpoints are stubbed in the backend and ready to be wired to your preferred CSV format.</p>
          <div className="mt-4 flex gap-2">
            <Button variant="outline" className="border-white/10 bg-white/5 text-white" disabled>Import CSV (contact team)</Button>
            <Button variant="outline" className="border-white/10 bg-white/5 text-white" disabled>Export CSV (contact team)</Button>
          </div>
        </section>
      </div>
    </div>
  );
}
